const FORM_CONTROL_SELECTOR = 'input,select,textarea';

let generatedControlId = 0;

function identifierHint(control) {
  const raw = control.getAttribute('aria-label')
    || control.getAttribute('placeholder')
    || control.getAttribute('title')
    || control.getAttribute('data-testid')
    || control.tagName.toLowerCase();
  const normalized = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || control.tagName.toLowerCase();
}

function collectIds(root) {
  return new Set(Array.from(root.querySelectorAll('[id]'))
    .map((element) => String(element.id || '').trim())
    .filter(Boolean));
}

/**
 * Ensures controls from legacy screens and dynamically mounted modules have a
 * stable DOM identifier. Existing ids/names are never changed. This keeps
 * browser autofill/accessibility diagnostics clean without changing any form
 * payload or onChange contract used by the ERP.
 */
export function ensureFormControlIdentifiers(root = document) {
  if (!root?.querySelectorAll) return 0;
  const usedIds = collectIds(root);
  let assigned = 0;
  const controls = [];
  if (root.matches?.(FORM_CONTROL_SELECTOR)) controls.push(root);
  controls.push(...root.querySelectorAll(FORM_CONTROL_SELECTOR));

  controls.forEach((control) => {
    if (control.id || control.name) return;

    let id;
    do {
      generatedControlId += 1;
      id = `nivora-field-${identifierHint(control)}-${generatedControlId}`;
    } while (usedIds.has(id));

    control.id = id;
    control.setAttribute('data-nivora-generated-id', 'true');
    usedIds.add(id);
    assigned += 1;
  });

  return assigned;
}

export function installFormControlIdentifierGuard(root = document) {
  if (!root?.querySelectorAll) return () => {};

  ensureFormControlIdentifiers(root);
  if (typeof MutationObserver !== 'function') return () => {};

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        ensureFormControlIdentifiers(node);
      });
    });
  });
  observer.observe(root.body || root, { childList: true, subtree: true });

  return () => observer.disconnect();
}
